import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Phone, ChevronDown, Menu, X } from 'lucide-react';
import { business, logoUrl } from '../../mock';
import { suburbs } from '../../data/suburbs';

// Primary top-level nav
const topNav = [
  { label: 'Home', href: '/', type: 'route' },
  { label: 'Residential Services', href: '#services', type: 'anchor' },
  { label: 'Commercial Services', href: '/commercial', type: 'route' },
  { label: 'Insights & Advice', href: '/blog', type: 'route' },
  { label: 'Recent Projects', href: '#gallery', type: 'anchor' },
  { label: 'Service Areas', href: '/service-areas', type: 'route', hasSuburbs: true },
  { label: 'Contact', href: '#contact', type: 'anchor' },
  { label: 'Customer Login', href: '/portal', type: 'route' },
];

// Featured suburbs shown in the header dropdown. Keeping this list SHORT
// (was previously all 53) dramatically reduces the shared content across
// suburb pages — critical for avoiding Google's soft-404 / duplicate-content
// classifier on template pages.
const FEATURED_SUBURB_SLUGS = [
  'ballarat', 'ballarat-north', 'ballarat-east',
  'alfredton', 'lucas', 'sebastopol',
  'delacombe', 'wendouree',
];
const featuredSuburbs = FEATURED_SUBURB_SLUGS
  .map((slug) => suburbs.find((s) => s.slug === slug))
  .filter(Boolean);

export default function Header() {
  const [openMobile, setOpenMobile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const goAnchor = (href) => {
    setOpenMobile(false);
    const id = href.replace('#', '');
    if (location.pathname !== '/') {
      navigate(`/#${id}`);
      // scroll after navigation
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const goRoute = (href) => {
    setOpenMobile(false);
    navigate(href);
    window.scrollTo(0, 0);
  };

  const handleClick = (link) => {
    if (link.type === 'route') return goRoute(link.href);
    return goAnchor(link.href);
  };

  return (
    <header className="sticky top-0 z-50">
      {/* Main header */}
      <div className="bg-[#FAF8F2] border-b border-[#E2DAC5]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-[72px] flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 shrink-0">
            <Link to="/" onClick={() => window.scrollTo(0, 0)} className="flex items-center shrink-0">
              <img src={logoUrl} alt="Vic Garden and Lawn Maintenance logo" width="116" height="44" className="h-11 w-auto" />
            </Link>
          </div>

          <nav className="hidden xl:flex items-center gap-4 2xl:gap-6 flex-1 justify-center">
            {topNav.map((link) => (
              <div key={link.label} className="relative group">
                <button
                  onClick={() => handleClick(link)}
                  className="flex items-center gap-1 text-sm font-semibold text-[#0A2E18] hover:text-[#1B6E37] transition-colors py-2 whitespace-nowrap"
                >
                  {link.label}
                  {link.hasSuburbs && <ChevronDown className="w-4 h-4" />}
                </button>
                {link.hasSuburbs && (
                  <div className="absolute top-full right-0 mt-1 w-64 bg-white border border-[#E2DAC5] rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 py-2">
                    {featuredSuburbs.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/service-areas/${s.slug}`}
                        onClick={() => window.scrollTo(0, 0)}
                        className="block px-4 py-2 text-sm text-[#0A2E18] hover:bg-[#F2ECE0]"
                      >
                        {s.name}
                      </Link>
                    ))}
                    <Link
                      to="/service-areas"
                      onClick={() => window.scrollTo(0, 0)}
                      className="block px-4 py-2 text-sm font-bold text-[#1B6E37] hover:bg-[#F2ECE0] border-t border-[#E2DAC5] mt-1"
                    >
                      View all {suburbs.length} service areas →
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <button
            className="xl:hidden p-2 text-[#0A2E18]"
            onClick={() => setOpenMobile(!openMobile)}
            aria-label="Toggle menu"
          >
            {openMobile ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {openMobile && (
          <div className="xl:hidden border-t border-[#E2DAC5] px-4 py-4 space-y-2 max-h-[80vh] overflow-y-auto bg-[#FAF8F2]">
            {topNav.map((link) => (
              <div key={link.label}>
                <button onClick={() => handleClick(link)} className="block w-full text-left py-2 font-semibold text-[#0A2E18]">
                  {link.label}
                </button>
                {link.hasSuburbs && (
                  <div className="pl-4 space-y-1 pb-2">
                    {featuredSuburbs.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/service-areas/${s.slug}`}
                        onClick={() => { setOpenMobile(false); window.scrollTo(0, 0); }}
                        className="block py-1 text-sm text-[#0A2E18]/80"
                      >
                        {s.name}
                      </Link>
                    ))}
                    <Link
                      to="/service-areas"
                      onClick={() => { setOpenMobile(false); window.scrollTo(0, 0); }}
                      className="block py-1 text-sm font-bold text-[#1B6E37]"
                    >
                      View all {suburbs.length} →
                    </Link>
                  </div>
                )}
              </div>
            ))}
            <div className="pt-3 flex flex-col gap-2">
              <a href={`tel:${business.phone}`} className="flex items-center justify-center gap-2 px-5 py-2.5 border-2 border-[#0A2E18] text-[#0A2E18] font-bold rounded-md">
                <Phone className="w-4 h-4" />
                {business.phone}
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
